import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import ValeTransporteForm, { ValeTransporteData } from './ValeTransporteForm';


interface ContratacaoEditTabsProps {
  contratacaoId: string;
  onSave: () => void;
  onCancel: () => void;
}

interface ModeloContrato {
  id: string;
  nome_modelo: string;
}

interface ModeloProposta {
  id: string;
  nome_modelo: string;
}

interface Cargo {
  id: string;
  nome: string;
}

interface FormData {
  // Dados
  email: string;
  nome_completo: string;
  cpf: string;
  celular: string;
  cargo_id: string;
  salario: string;
  jornada_trabalho: string;
  horario_entrada: string;
  horario_saida: string;
  data_inicio: string;
  observacoes: string;
  razao_social: string;
  cnpj: string;
  
  // Proposta - agora suporta múltiplos modelos
  modelos_proposta_selecionados: string[];
  
  // Vale Transporte
  vale_transporte_valor_ida: number;
  vale_transporte_valor_volta: number;
  valeTransporteData: ValeTransporteData;
  
  // Exame
  endereco_exame: string;
  horario_exame: string;
  guia_encaminhamento: File | null;
  
  // Contrato
  modelos_selecionados: string[];
  prazo_assinatura: string;
  
  // Integração
  quem_recebera_contratado: string;
}

export default function ContratacaoEditTabs({ contratacaoId, onSave, onCancel }: ContratacaoEditTabsProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("dados");
  const [contratacaoStage, setContratacaoStage] = useState<string>('inicial');
  const [modelos, setModelos] = useState<ModeloContrato[]>([]);
  const [modelosPropostas, setModelosPropostas] = useState<ModeloProposta[]>([]);
  const [cargos, setCargos] = useState<Cargo[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialFormData, setInitialFormData] = useState<FormData | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [propostaPersonalizada, setPropostaPersonalizada] = useState<string>('');
  const [modelosDetalhados, setModelosDetalhados] = useState<{[key: string]: {nome: string, html: string}}>({});
  const [dadosPessoais, setDadosPessoais] = useState<any>(null);
  const [htmlProcessado, setHtmlProcessado] = useState<{[key: string]: string}>({});
  
  const [formData, setFormData] = useState<FormData>({
    email: '',
    nome_completo: '',
    cpf: '',
    celular: '',
    cargo_id: '',
    salario: '',
    jornada_trabalho: '',
    horario_entrada: '',
    horario_saida: '',
    data_inicio: '',
    observacoes: '',
    razao_social: '',
    cnpj: '',
    modelos_proposta_selecionados: [],
    vale_transporte_valor_ida: 0,
    vale_transporte_valor_volta: 0,
    valeTransporteData: {
      ida_integracao_onibus_metro: false,
      ida_integracao_onibus_metro_valor: 0,
      ida_integracao_onibus_trem: false,
      ida_integracao_onibus_trem_valor: 0,
      ida_onibus_municipal: false,
      ida_onibus_municipal_valor: 0,
      ida_onibus_intermunicipal: false,
      ida_onibus_intermunicipal_valor: 0,
      ida_metro_trem: false,
      ida_metro_trem_valor: 0,
      ida_outros: false,
      ida_outros_descricao: '',
      ida_outros_valor: 0,
      volta_integracao_onibus_metro: false,
      volta_integracao_onibus_metro_valor: 0,
      volta_integracao_onibus_trem: false,
      volta_integracao_onibus_trem_valor: 0,
      volta_onibus_municipal: false,
      volta_onibus_municipal_valor: 0,
      volta_onibus_intermunicipal: false,
      volta_onibus_intermunicipal_valor: 0,
      volta_metro_trem: false,
      volta_metro_trem_valor: 0,
      volta_outros: false,
      volta_outros_descricao: '',
      volta_outros_valor: 0,
      tipo_cartao: 'comum',
      tipo_cartao_outros: ''
    },
    endereco_exame: '',
    horario_exame: '',
    guia_encaminhamento: null,
    modelos_selecionados: [],
    prazo_assinatura: '',
    quem_recebera_contratado: ''
  });

  useEffect(() => {
    fetchContratacao();
    fetchModelos();
    fetchModelosPropostas();
    fetchCargos();
  }, [contratacaoId]);

  // Buscar dados pessoais quando o CPF for alterado
  useEffect(() => {
    const fetchDadosPessoais = async () => {
      if (formData.cpf || contratacaoId) {
        try {
          let data = null;
          
          // Primeiro, tentar buscar pelo CPF
          if (formData.cpf) {
            console.log('=== BUSCANDO DADOS PESSOAIS POR CPF ===');
            console.log('CPF:', formData.cpf);
            try {
              const result = await supabase
                .from('dados_pessoais')
                .select('*')
                .eq('cpf', formData.cpf)
                .order('created_at', { ascending: false })
                .limit(1);
              
              console.log('Query result:', result);
              
              if (result.data && result.data.length > 0) {
                data = result.data[0];
                console.log('Dados encontrados por CPF:', data);
                console.log('Cor da pele encontrada:', data?.cor_pele);
              } else {
                console.log('Nenhum dado encontrado por CPF');
              }
            } catch (error) {
              console.error('Erro na busca por CPF:', error);
            }
          }
          
          // Se não encontrou pelo CPF, buscar pelo user_id da contratação
          if (!data && contratacaoId) {
            // Primeiro buscar o user_id da contratação
            const { data: contratacaoData } = await supabase
              .from('contratacao')
              .select('user_criado, id_usuario')
              .eq('id', contratacaoId)
              .maybeSingle();
            
            if (contratacaoData?.user_criado) {
              const result = await supabase
                .from('dados_pessoais')
                .select('*')
                .eq('user_id', contratacaoData.user_criado)
                .maybeSingle();
              data = result.data;
            }
            
            if (!data && contratacaoData?.id_usuario) {
              const result = await supabase
                .from('dados_pessoais')
                .select('*')
                .eq('user_id', contratacaoData.id_usuario)
                .maybeSingle();
              data = result.data;
            }
          }
          
          setDadosPessoais(data);
          console.log('Dados pessoais encontrados:', data);
        } catch (error) {
          console.error('Erro ao buscar dados pessoais:', error);
        }
      }
    };
    
    fetchDadosPessoais();
  }, [formData.cpf, contratacaoId]);

  // Processar HTML dos contratos quando dados estiverem disponíveis
  useEffect(() => {
    const processarHtmlContratos = async () => {
      if (formData.modelos_selecionados.length > 0 && formData.cargo_id) {
        const novoHtmlProcessado: {[key: string]: string} = {};
        
        for (const modeloId of formData.modelos_selecionados) {
          const modeloDetalhado = modelosDetalhados[modeloId];
          if (modeloDetalhado) {
            try {
              const htmlSubstituido = await substituirDadosContrato(modeloDetalhado.html, dadosPessoais);
              novoHtmlProcessado[modeloId] = htmlSubstituido;
            } catch (error) {
              console.error('Erro ao processar HTML do contrato:', error);
            }
          }
        }
        
        setHtmlProcessado(novoHtmlProcessado);
      }
    };

    processarHtmlContratos();
  }, [formData.modelos_selecionados, formData.cargo_id, modelosDetalhados, dadosPessoais, formData.data_inicio, formData.salario, formData.prazo_assinatura]);

  // Atualizar preview de proposta automaticamente
  useEffect(() => {
    const atualizarPreviewProposta = async () => {
      if (formData.modelos_proposta_selecionados.length > 0) {
        const propostaHtml = await personalizarProposta();
        if (propostaHtml) {
          setPropostaPersonalizada(propostaHtml);
        }
      } else {
        setPropostaPersonalizada('');
      }
    };

    atualizarPreviewProposta();
  }, [formData.modelos_proposta_selecionados, formData.cargo_id, formData.nome_completo, formData.data_inicio, formData.salario]);

  // Detectar alterações nos dados
  useEffect(() => {
    if (initialFormData) {
      const hasChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData);
      setHasChanges(hasChanged);
    }
  }, [formData, initialFormData]);

  const fetchContratacao = async () => {
    try {
      const { data, error } = await supabase
        .from('contratacao')
        .select('*')
        .eq('id', contratacaoId)
        .single();

      if (error) throw error;

      // Converter data_inicio para formato local YYYY-MM-DD
      let dataInicioLocal = '';
      if ((data as any).data_inicio) {
        const dataInicio = new Date((data as any).data_inicio);
        dataInicioLocal = `${dataInicio.getFullYear()}-${String(dataInicio.getMonth() + 1).padStart(2, '0')}-${String(dataInicio.getDate()).padStart(2, '0')}`;
      }

      // Buscar dados detalhados do vale transporte usando contratacao_id
      let valeTransporteDetalhado = null;
      try {
        const { data: vtData } = await supabase
          .from('vale_transporte_detalhado')
          .select('*')
          .eq('contratacao_id', contratacaoId)
          .maybeSingle();
        valeTransporteDetalhado = vtData;
      } catch (error) {
        console.error('Erro ao buscar vale transporte detalhado:', error);
      }

      const initialData = {
        email: data.email || '',
        nome_completo: data.nome_completo || '',
        cpf: data.cpf || '',
        celular: data.celular || '',
        cargo_id: data.cargo_id || '',
        salario: data.salario ? data.salario.toString() : '',
        jornada_trabalho: data.jornada_trabalho || '',
        horario_entrada: data.horario_entrada || '',
        horario_saida: data.horario_saida || '',
        data_inicio: dataInicioLocal,
        observacoes: data.observacoes || '',
        razao_social: data.razao_social || '',
        cnpj: data.cnpj || '',
        modelos_proposta_selecionados: data.ids_modelos_proposta || [],
        vale_transporte_valor_ida: data.vale_transporte_valor_ida || 0,
        vale_transporte_valor_volta: data.vale_transporte_valor_volta || 0,
        valeTransporteData: valeTransporteDetalhado ? {
          ida_integracao_onibus_metro: valeTransporteDetalhado.ida_integracao_onibus_metro || false,
          ida_integracao_onibus_metro_valor: valeTransporteDetalhado.ida_integracao_onibus_metro_valor || 0,
          ida_integracao_onibus_trem: valeTransporteDetalhado.ida_integracao_onibus_trem || false,
          ida_integracao_onibus_trem_valor: valeTransporteDetalhado.ida_integracao_onibus_trem_valor || 0,
          ida_onibus_municipal: valeTransporteDetalhado.ida_onibus_municipal || false,
          ida_onibus_municipal_valor: valeTransporteDetalhado.ida_onibus_municipal_valor || 0,
          ida_onibus_intermunicipal: valeTransporteDetalhado.ida_onibus_intermunicipal || false,
          ida_onibus_intermunicipal_valor: valeTransporteDetalhado.ida_onibus_intermunicipal_valor || 0,
          ida_metro_trem: valeTransporteDetalhado.ida_metro_trem || false,
          ida_metro_trem_valor: valeTransporteDetalhado.ida_metro_trem_valor || 0,
          ida_outros: valeTransporteDetalhado.ida_outros || false,
          ida_outros_descricao: valeTransporteDetalhado.ida_outros_descricao || '',
          ida_outros_valor: valeTransporteDetalhado.ida_outros_valor || 0,
          volta_integracao_onibus_metro: valeTransporteDetalhado.volta_integracao_onibus_metro || false,
          volta_integracao_onibus_metro_valor: valeTransporteDetalhado.volta_integracao_onibus_metro_valor || 0,
          volta_integracao_onibus_trem: valeTransporteDetalhado.volta_integracao_onibus_trem || false,
          volta_integracao_onibus_trem_valor: valeTransporteDetalhado.volta_integracao_onibus_trem_valor || 0,
          volta_onibus_municipal: valeTransporteDetalhado.volta_onibus_municipal || false,
          volta_onibus_municipal_valor: valeTransporteDetalhado.volta_onibus_municipal_valor || 0,
          volta_onibus_intermunicipal: valeTransporteDetalhado.volta_onibus_intermunicipal || false,
          volta_onibus_intermunicipal_valor: valeTransporteDetalhado.volta_onibus_intermunicipal_valor || 0,
          volta_metro_trem: valeTransporteDetalhado.volta_metro_trem || false,
          volta_metro_trem_valor: valeTransporteDetalhado.volta_metro_trem_valor || 0,
          volta_outros: valeTransporteDetalhado.volta_outros || false,
          volta_outros_descricao: valeTransporteDetalhado.volta_outros_descricao || '',
          volta_outros_valor: valeTransporteDetalhado.volta_outros_valor || 0,
          tipo_cartao: valeTransporteDetalhado.tipo_cartao || 'comum',
          tipo_cartao_outros: valeTransporteDetalhado.tipo_cartao_outros || ''
        } : {
          ida_integracao_onibus_metro: false,
          ida_integracao_onibus_metro_valor: 0,
          ida_integracao_onibus_trem: false,
          ida_integracao_onibus_trem_valor: 0,
          ida_onibus_municipal: false,
          ida_onibus_municipal_valor: 0,
          ida_onibus_intermunicipal: false,
          ida_onibus_intermunicipal_valor: 0,
          ida_metro_trem: false,
          ida_metro_trem_valor: 0,
          ida_outros: false,
          ida_outros_descricao: '',
          ida_outros_valor: 0,
          volta_integracao_onibus_metro: false,
          volta_integracao_onibus_metro_valor: 0,
          volta_integracao_onibus_trem: false,
          volta_integracao_onibus_trem_valor: 0,
          volta_onibus_municipal: false,
          volta_onibus_municipal_valor: 0,
          volta_onibus_intermunicipal: false,
          volta_onibus_intermunicipal_valor: 0,
          volta_metro_trem: false,
          volta_metro_trem_valor: 0,
          volta_outros: false,
          volta_outros_descricao: '',
          volta_outros_valor: 0,
          tipo_cartao: 'comum',
          tipo_cartao_outros: ''
        },
        endereco_exame: data.endereco_exame || '',
        horario_exame: data.horario_exame || '',
        guia_encaminhamento: null,
        modelos_selecionados: data.ids_modelos_contrato || [],
        prazo_assinatura: data.prazo_assinatura || '',
        quem_recebera_contratado: data.quem_recebera_contratado || ''
      };

      setFormData(initialData);
      setInitialFormData(initialData);
      setContratacaoStage(data.stage || 'inicial');
      
      // Carregar modelos detalhados se já existirem selecionados
      if (data.ids_modelos_contrato && data.ids_modelos_contrato.length > 0) {
        data.ids_modelos_contrato.forEach((modeloId: string) => {
          fetchModeloDetalhado(modeloId);
        });
      }
      
      // Carregar a proposta HTML se existir
      if ((data as any).proposta_html) {
        setPropostaPersonalizada((data as any).proposta_html);
      }
    } catch (error: any) {
      toast({
        title: "Erro ao carregar contratação",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const fetchModelos = async () => {
    try {
      const { data, error } = await supabase
        .from('tbl_modelo_contrato')
        .select('id, nome_modelo')
        .eq('status', 'ativo');

      if (error) throw error;
      setModelos(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar modelos",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const fetchModelosPropostas = async () => {
    try {
      const { data, error } = await supabase
        .from('modelo_proposta')
        .select('id, nome_modelo')
        .eq('status', 'ativo');

      if (error) throw error;
      setModelosPropostas(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar modelos de proposta",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const fetchCargos = async () => {
    try {
      const { data, error } = await supabase
        .from('cargos')
        .select('id, nome')
        .eq('status', 'ativo')
        .order('nome');

      if (error) throw error;
      setCargos(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar cargos",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const isTabValid = (tab: string): boolean => {
    switch (tab) {
      case 'dados':
        return !!(formData.email && formData.nome_completo && formData.cpf && formData.cargo_id && formData.salario && formData.jornada_trabalho && formData.horario_entrada && formData.horario_saida && formData.data_inicio);
      case 'proposta':
        return formData.modelos_proposta_selecionados.length > 0;
      case 'vale_transporte':
        return true; // Sempre válido pois os valores têm default
      case 'exame':
        return !!(formData.endereco_exame && formData.horario_exame);
      case 'contrato':
        return formData.modelos_selecionados.length > 0 && !!formData.prazo_assinatura;
      case 'integracao':
        return !!formData.quem_recebera_contratado;
      default:
        return false;
    }
  };

  const canAccessTab = (tab: string): boolean => {
    // Todas as abas sempre acessíveis
    return true;
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleModeloToggle = async (modeloId: string) => {
    const isSelected = formData.modelos_selecionados.includes(modeloId);
    
    setFormData(prev => ({
      ...prev,
      modelos_selecionados: isSelected
        ? prev.modelos_selecionados.filter(id => id !== modeloId)
        : [...prev.modelos_selecionados, modeloId]
    }));

    // Se o modelo está sendo selecionado, buscar o HTML detalhado
    if (!isSelected) {
      await fetchModeloDetalhado(modeloId);
    }
  };

  const handleModeloPropostaToggle = (modeloId: string) => {
    setFormData(prev => ({
      ...prev,
      modelos_proposta_selecionados: prev.modelos_proposta_selecionados.includes(modeloId)
        ? prev.modelos_proposta_selecionados.filter(id => id !== modeloId)
        : [...prev.modelos_proposta_selecionados, modeloId]
    }));
    setHasChanges(true);
  };

  const fetchModeloDetalhado = async (modeloId: string) => {
    try {
      const { data, error } = await supabase
        .from('tbl_modelo_contrato')
        .select('id, nome_modelo, conteudo_html')
        .eq('id', modeloId)
        .single();

      if (error) throw error;

      setModelosDetalhados(prev => ({
        ...prev,
        [modeloId]: {
          nome: data.nome_modelo,
          html: data.conteudo_html
        }
      }));
    } catch (error: any) {
      toast({
        title: "Erro ao carregar modelo",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const personalizarProposta = async () => {
    try {
      if (formData.modelos_proposta_selecionados.length === 0) return '';

      // Buscar TODOS os modelos selecionados
      const { data: modelosData, error: modelosError } = await supabase
        .from('modelo_proposta')
        .select('id, nome_modelo, conteudo_html')
        .in('id', formData.modelos_proposta_selecionados);

      if (modelosError) throw modelosError;
      if (!modelosData || modelosData.length === 0) return '';

      // Buscar dados do cargo
      const { data: cargoData } = await supabase
        .from('cargos')
        .select('nome, departamento')
        .eq('id', formData.cargo_id)
        .maybeSingle();

      // Data atual para formatação
      const hoje = new Date();
      const meses = [
        'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
        'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
      ];

      // Formatar data de início se existir
      let dataInicioFormatada = formData.data_inicio || 'A definir';
      let dataInicioExtenso = 'A definir';
      let diaInicio = '';
      let mesInicio = '';
      let anoInicio = '';
      
      if (formData.data_inicio) {
        const [ano, mes, dia] = formData.data_inicio.split('-').map(Number);
        dataInicioFormatada = `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${ano}`;
        mesInicio = meses[mes - 1];
        dataInicioExtenso = `${dia} de ${mesInicio} de ${ano}`;
        diaInicio = dia.toString();
        anoInicio = ano.toString();
      }

      // Processar TODOS os modelos e concatenar
      const propostasProcessadas = modelosData.map((modelo, index) => {
        let propostaPersonalizada = modelo.conteudo_html;

        // PROCESSAR PADRÕES COMPOSTOS PRIMEIRO (evita repetição)
        propostaPersonalizada = propostaPersonalizada
          .replace(/@data@\s+de\s+@mes@\s+de\s+@ano@/gi, 
            `${hoje.getDate()} de ${meses[hoje.getMonth()]} de ${hoje.getFullYear()}`)
          .replace(/@dia_inicio@\s+de\s+@mes_inicio@\s+de\s+@ano_inicio@/gi, 
            dataInicioExtenso);

        // Personalizar proposta com os dados
        propostaPersonalizada = propostaPersonalizada
          .replace(/@nome_completo@/g, formData.nome_completo || 'Não informado')
          .replace(/@dataInicio@/gi, dataInicioFormatada)
          .replace(/@data_inicio@/gi, dataInicioFormatada)
          .replace(/@data_inicio_extenso@/gi, dataInicioExtenso)
          .replace(/@dia_inicio@/gi, diaInicio)
          .replace(/@mes_inicio@/gi, mesInicio)
          .replace(/@ano_inicio@/gi, anoInicio)
          .replace(/@cargo@/g, cargoData?.nome || 'Não informado')
          .replace(/@departamento@/g, cargoData?.departamento || 'Não informado')
          .replace(/@salario@/g, formData.salario ? `R$ ${parseFloat(formData.salario).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'A definir')
          .replace(/@cpf@/g, formData.cpf || 'Não informado')
          .replace(/@horario_entrada@/g, formData.horario_entrada || '00:00')
          .replace(/@horario_saida@/g, formData.horario_saida || '00:00')
          .replace(/@razao_social@/g, formData.razao_social || 'Não informado')
          .replace(/@cnpj@/g, formData.cnpj || 'Não informado')
          .replace(/@data@/g, `${hoje.getDate()} de ${meses[hoje.getMonth()]} de ${hoje.getFullYear()}`)
          .replace(/@dia@/g, hoje.getDate().toString())
          .replace(/@mes@/g, meses[hoje.getMonth()])
          .replace(/@ano@/g, hoje.getFullYear().toString());

        // Adicionar cabeçalho com nome do modelo e separador
        const cabecalho = `
          <div style="background: #f0f0f0; padding: 15px; margin: 20px 0; border-left: 4px solid #2563eb;">
            <h2 style="margin: 0; color: #1e40af;">PROPOSTA ${index + 1} de ${modelosData.length}: ${modelo.nome_modelo}</h2>
          </div>
        `;
        
        return cabecalho + propostaPersonalizada;
      });

      // Juntar todas as propostas
      return propostasProcessadas.join('<hr style="margin: 40px 0; border: 2px solid #e5e7eb;" />');
    } catch (error: any) {
      toast({
        title: "Erro ao personalizar proposta",
        description: error.message,
        variant: "destructive",
      });
      return '';
    }
  };

  const handleModeloPropostaChange = async (value: string) => {
    setFormData(prev => ({ ...prev, modelos_proposta_selecionados: [value] }));
    
    // SEMPRE personalizar proposta automaticamente quando o modelo for selecionado
    if (value) {
      // Criar dados temporários com o novo modelo selecionado
      const dadosTemp = { ...formData, modelos_proposta_selecionados: [value] };
      
      const propostaHTML = await personalizarPropostaComDados(dadosTemp);
      if (propostaHTML) {
        setPropostaPersonalizada(propostaHTML);
        
        // Salvar a proposta personalizada no banco
        try {
          const { error } = await supabase
            .from('contratacao')
            .update({ 
              proposta_html: propostaHTML,
              ids_modelos_proposta: [value]
            })
            .eq('id', contratacaoId);

          if (error) throw error;
          
          toast({
            title: "Sucesso",
            description: "Proposta personalizada com sucesso!",
          });
        } catch (error: any) {
          console.error('Erro ao salvar proposta:', error);
          toast({
            title: "Erro",
            description: "Erro ao salvar proposta",
            variant: "destructive",
          });
        }
      }
    } else {
      setPropostaPersonalizada('');
    }
  };

  const personalizarPropostaComDados = async (dadosForm: FormData) => {
    try {
      if (dadosForm.modelos_proposta_selecionados.length === 0) return '';

      // Pegar o primeiro modelo selecionado
      const primeiroModelo = dadosForm.modelos_proposta_selecionados[0];

      // Buscar o modelo de proposta
      const { data: modeloData, error: modeloError } = await supabase
        .from('modelo_proposta')
        .select('conteudo_html')
        .eq('id', primeiroModelo)
        .single();

      if (modeloError) throw modeloError;

      let propostaPersonalizada = modeloData.conteudo_html;

      // Buscar dados do cargo
      const { data: cargoData } = await supabase
        .from('cargos')
        .select('nome, departamento')
        .eq('id', dadosForm.cargo_id)
        .maybeSingle();

      // Data atual para formatação
      const hoje = new Date();
      const meses = [
        'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
        'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
      ];

      // Formatar data de início se existir
      let dataInicioFormatada = dadosForm.data_inicio || 'A definir';
      if (dadosForm.data_inicio) {
        // Criar a data usando os componentes do formato YYYY-MM-DD para evitar problemas de timezone
        const [ano, mes, dia] = dadosForm.data_inicio.split('-').map(Number);
        dataInicioFormatada = `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${ano}`;
      }

      // Personalizar proposta com os dados
      propostaPersonalizada = propostaPersonalizada
        .replace(/@nome_completo@/g, dadosForm.nome_completo || 'Não informado')
        .replace(/@dataInicio@/gi, dataInicioFormatada)
        .replace(/@data_inicio@/gi, dataInicioFormatada)
        .replace(/@cargo@/g, cargoData?.nome || 'Não informado')
        .replace(/@departamento@/g, cargoData?.departamento || 'Não informado')
        .replace(/@salario@/g, dadosForm.salario ? `R$ ${parseFloat(dadosForm.salario).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'A definir')
        .replace(/@cpf@/g, dadosForm.cpf || 'Não informado')
        .replace(/@horario_entrada@/g, dadosForm.horario_entrada || '00:00')
        .replace(/@horario_saida@/g, dadosForm.horario_saida || '00:00')
        .replace(/@razao_social@/g, dadosForm.razao_social || 'Não informado')
        .replace(/@cnpj@/g, dadosForm.cnpj || 'Não informado')
        .replace(/@dia@/g, hoje.getDate().toString())
        .replace(/@mes@/g, meses[hoje.getMonth()])
        .replace(/@ano@/g, hoje.getFullYear().toString())
        .replace(/@data@/g, `${hoje.getDate()} de ${meses[hoje.getMonth()]} de ${hoje.getFullYear()}`);

      return propostaPersonalizada;
    } catch (error: any) {
      toast({
        title: "Erro ao personalizar proposta",
        description: error.message,
        variant: "destructive",
      });
      return '';
    }
  };

  const substituirDadosContrato = async (htmlContrato: string, dadosPessoais: any = null, contratacaoIdParam: string | null = null) => {
    let htmlSubstituido = htmlContrato;
    
    console.log('Dados pessoais para substituição:', dadosPessoais);
    console.log('FormData para substituição:', formData);
    console.log('ContratacaoIdParam para busca direta:', contratacaoIdParam);
    
    // Determinar cargo_id - preferir busca direta da contratação se disponível
    let cargoIdParaUsar = formData.cargo_id;
    
    if (contratacaoIdParam) {
      const { data: contratacaoData } = await supabase
        .from('contratacao')
        .select('cargo_id')
        .eq('id', contratacaoIdParam)
        .maybeSingle();
      
      if (contratacaoData?.cargo_id) {
        cargoIdParaUsar = contratacaoData.cargo_id;
        console.log('cargo_id obtido diretamente da contratação:', cargoIdParaUsar);
      }
    }
    
    // Buscar cargo nome e departamento se existir cargo_id
    let cargoNome = '';
    let cargoDepartamento = '';
    if (cargoIdParaUsar) {
      const { data: cargoData } = await supabase
        .from('cargos')
        .select('nome, departamento')
        .eq('id', cargoIdParaUsar)
        .maybeSingle();
      cargoNome = cargoData?.nome || '';
      cargoDepartamento = cargoData?.departamento || '';
      console.log('Cargo encontrado:', cargoNome, '- Departamento:', cargoDepartamento);
    }

    // Data atual para São Paulo, dia de mês de ano
    const dataAtual = new Date();
    const dia = dataAtual.getDate();
    const meses = [
      'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
      'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
    ];
    const mes = meses[dataAtual.getMonth()];
    const ano = dataAtual.getFullYear();
    
    // Formatar data de início se existir
    let dataInicioFormatada = formData.data_inicio || '';
    let dataInicioExtenso = '';
    let diaInicio = '';
    let mesInicio = '';
    let anoInicio = '';
    
    if (formData.data_inicio) {
      const [anoInit, mesInit, diaInit] = formData.data_inicio.split('-').map(Number);
      dataInicioFormatada = `${String(diaInit).padStart(2, '0')}/${String(mesInit).padStart(2, '0')}/${anoInit}`;
      mesInicio = meses[mesInit - 1];
      dataInicioExtenso = `${diaInit} de ${mesInicio} de ${anoInit}`;
      diaInicio = diaInit.toString();
      anoInicio = anoInit.toString();
    }
    
    // PROCESSAR PADRÕES COMPOSTOS PRIMEIRO (evita repetição)
    htmlSubstituido = htmlSubstituido
      .replace(/@data@\s+de\s+@mes@\s+de\s+@ano@/gi, 
        `${dia} de ${mes} de ${ano}`)
      .replace(/@dia_inicio@\s+de\s+@mes_inicio@\s+de\s+@ano_inicio@/gi, 
        dataInicioExtenso);
    
    // Substituir dados da tabela contratacao
    htmlSubstituido = htmlSubstituido.replace(/@data_inicio@/gi, dataInicioFormatada);
    htmlSubstituido = htmlSubstituido.replace(/@dataInicio@/gi, dataInicioFormatada);
    htmlSubstituido = htmlSubstituido.replace(/@data_inicio_extenso@/gi, dataInicioExtenso);
    htmlSubstituido = htmlSubstituido.replace(/@dia_inicio@/gi, diaInicio);
    htmlSubstituido = htmlSubstituido.replace(/@mes_inicio@/gi, mesInicio);
    htmlSubstituido = htmlSubstituido.replace(/@ano_inicio@/gi, anoInicio);
    htmlSubstituido = htmlSubstituido.replace(/@cargo@/gi, cargoNome);
    htmlSubstituido = htmlSubstituido.replace(/@departamento@/gi, cargoDepartamento);
    htmlSubstituido = htmlSubstituido.replace(/@salario@/gi, formData.salario ? `R$ ${parseFloat(formData.salario).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '');
    htmlSubstituido = htmlSubstituido.replace(/@horario_entrada@/gi, formData.horario_entrada || '00:00');
    htmlSubstituido = htmlSubstituido.replace(/@horario_saida@/gi, formData.horario_saida || '00:00');
    htmlSubstituido = htmlSubstituido.replace(/@razao_social@/gi, formData.razao_social || '');
    htmlSubstituido = htmlSubstituido.replace(/@cnpj@/gi, formData.cnpj || '');
    htmlSubstituido = htmlSubstituido.replace(/@data@/gi, `${dia} de ${mes} de ${ano}`);
    htmlSubstituido = htmlSubstituido.replace(/@dia@/gi, dia.toString());
    htmlSubstituido = htmlSubstituido.replace(/@mes@/gi, mes);
    htmlSubstituido = htmlSubstituido.replace(/@ano@/gi, ano.toString());
    
    // Função para formatar estado civil
    const formatarEstadoCivil = (estadoCivil: string) => {
      if (!estadoCivil) return '';
      return estadoCivil.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    // Substituir dados da tabela dados_pessoais (se existirem)
    if (dadosPessoais) {
      console.log('=== DEBUG COR_PELE ===');
      console.log('Dados pessoais completos:', dadosPessoais);
      console.log('Valor cor_pele:', dadosPessoais.cor_pele);
      console.log('Tipo cor_pele:', typeof dadosPessoais.cor_pele);
      console.log('HTML antes da substituição cor_pele:', htmlSubstituido.includes('@cor_pele@') ? 'Contém @cor_pele@' : 'Não contém @cor_pele@');
      
      htmlSubstituido = htmlSubstituido.replace(/@nome_completo@/gi, dadosPessoais.nome_completo || formData.nome_completo || '');
      htmlSubstituido = htmlSubstituido.replace(/@nome_social@/gi, dadosPessoais.nome_social || '');
      htmlSubstituido = htmlSubstituido.replace(/@nacionalidade@/gi, dadosPessoais.nacionalidade || '');
      htmlSubstituido = htmlSubstituido.replace(/@estado_civil@/gi, formatarEstadoCivil(dadosPessoais.estado_civil || ''));
      htmlSubstituido = htmlSubstituido.replace(/@cidade@/gi, dadosPessoais.cidade || '');
      htmlSubstituido = htmlSubstituido.replace(/@ctps@/gi, dadosPessoais.carteira_trabalho || '');
      htmlSubstituido = htmlSubstituido.replace(/@carteira_trabalho@/gi, dadosPessoais.carteira_trabalho || '');
      htmlSubstituido = htmlSubstituido.replace(/@rg@/gi, dadosPessoais.rg || '');
      htmlSubstituido = htmlSubstituido.replace(/@cpf@/gi, dadosPessoais.cpf || formData.cpf || '');
      htmlSubstituido = htmlSubstituido.replace(/@pis@/gi, dadosPessoais.numero_pis || '');
      htmlSubstituido = htmlSubstituido.replace(/@numero_pis@/gi, dadosPessoais.numero_pis || '');
      htmlSubstituido = htmlSubstituido.replace(/@logradouro@/gi, dadosPessoais.logradouro || '');
      htmlSubstituido = htmlSubstituido.replace(/@numero@/gi, dadosPessoais.numero || '');
      htmlSubstituido = htmlSubstituido.replace(/@bairro@/gi, dadosPessoais.bairro || '');
      htmlSubstituido = htmlSubstituido.replace(/@estado@/gi, dadosPessoais.estado || '');
      htmlSubstituido = htmlSubstituido.replace(/@cep@/gi, dadosPessoais.cep || '');
      htmlSubstituido = htmlSubstituido.replace(/@cor_pele@/gi, dadosPessoais.cor_pele || '');
      
      console.log('Valor usado para substituir cor_pele:', dadosPessoais.cor_pele || '');
      console.log('HTML após substituição cor_pele:', htmlSubstituido.includes('@cor_pele@') ? 'AINDA contém @cor_pele@' : 'Substituído com sucesso');
      console.log('=== FIM DEBUG COR_PELE ===');
    } else {
      // Se não tem dados pessoais, usar dados da contratação como fallback
      htmlSubstituido = htmlSubstituido.replace(/@nome_completo@/gi, formData.nome_completo || '');
      htmlSubstituido = htmlSubstituido.replace(/@cpf@/gi, formData.cpf || '');
      // Para os outros campos que não existem na contratação, deixar vazio
      htmlSubstituido = htmlSubstituido.replace(/@nacionalidade@/gi, '');
      htmlSubstituido = htmlSubstituido.replace(/@estado_civil@/gi, '');
      htmlSubstituido = htmlSubstituido.replace(/@cidade@/gi, '');
      htmlSubstituido = htmlSubstituido.replace(/@ctps@/gi, '');
      htmlSubstituido = htmlSubstituido.replace(/@carteira_trabalho@/gi, '');
      htmlSubstituido = htmlSubstituido.replace(/@rg@/gi, '');
      htmlSubstituido = htmlSubstituido.replace(/@pis@/gi, '');
      htmlSubstituido = htmlSubstituido.replace(/@numero_pis@/gi, '');
      htmlSubstituido = htmlSubstituido.replace(/@logradouro@/gi, '');
      htmlSubstituido = htmlSubstituido.replace(/@numero@/gi, '');
      htmlSubstituido = htmlSubstituido.replace(/@bairro@/gi, '');
      htmlSubstituido = htmlSubstituido.replace(/@estado@/gi, '');
      htmlSubstituido = htmlSubstituido.replace(/@cep@/gi, '');
      htmlSubstituido = htmlSubstituido.replace(/@cor_pele@/gi, '');
      htmlSubstituido = htmlSubstituido.replace(/@nome_social@/gi, '');
    }
    
    // Substituir prazo (será definido no momento da criação do contrato)
    htmlSubstituido = htmlSubstituido.replace(/@prazo@/gi, formData.prazo_assinatura || '');
    
    // Substituir cidade, dia, mês e ano da data atual (São Paulo) - fazer por último para não conflitar
    htmlSubstituido = htmlSubstituido.replace(/@dia@/gi, dia.toString());
    htmlSubstituido = htmlSubstituido.replace(/@mes@/gi, mes);
    htmlSubstituido = htmlSubstituido.replace(/@ano@/gi, ano.toString());
    // Fazer substituição da cidade por último para não conflitar com @cidade@ dos dados pessoais
    htmlSubstituido = htmlSubstituido.replace(/São Paulo, @dia@ de @mes@ de @ano@/gi, `São Paulo, ${dia} de ${mes} de ${ano}`);
    htmlSubstituido = htmlSubstituido.replace(/@cidade@, @dia@ de @mes@ de @ano@/gi, `São Paulo, ${dia} de ${mes} de ${ano}`);
    
    console.log('HTML após substituição:', htmlSubstituido.substring(0, 500));
    
    return htmlSubstituido;
  };

  const savePropostaToTable = async () => {
    try {
      const { data: user, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // Buscar o user_id do candidato pela contratação
      const { data: contratacaoData } = await supabase
        .from('contratacao')
        .select('id_usuario, user_criado, email')
        .eq('id', contratacaoId)
        .single();

      // Determinar o user_contratado (candidato)
      let userContratado = contratacaoData?.id_usuario || contratacaoData?.user_criado;
      
      // Se não encontrou user_id, buscar pelo email
      if (!userContratado && contratacaoData?.email) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('email', contratacaoData.email)
          .maybeSingle();
        
        userContratado = profileData?.user_id;
      }

      // Verificar se já existe uma proposta para esta contratação
      const { data: existingProposta } = await supabase
        .from('contratacao_contratos')
        .select('id')
        .eq('contratacao_id', contratacaoId)
        .eq('modelo_contrato_id', null) // Proposta não tem modelo_contrato_id
        .maybeSingle();

      if (existingProposta) {
        // Atualizar proposta existente
        const { error: updateError } = await supabase
          .from('contratacao_contratos')
          .update({
            html_contrato: propostaPersonalizada,
            updated_by: user.user?.id
          })
          .eq('id', existingProposta.id);

        if (updateError) throw updateError;
      } else {
        // Criar nova proposta na tabela contratacao_contratos
        // Buscar nome do modelo de proposta
        const modeloProposta = modelosPropostas.find(m => formData.modelos_proposta_selecionados.includes(m.id));
        const nomeModeloProposta = modeloProposta?.nome_modelo || 'Proposta';
        const cpfLimpo = formData.cpf.replace(/[^\d]/g, '');
        
        const proposta = {
          contratacao_id: contratacaoId,
          user_contratado: userContratado, // Usuário que deve assinar
          modelo_contrato_id: null, // Proposta não tem modelo_contrato_id específico
          html_contrato: propostaPersonalizada,
          bt_aceite: false,
          prazo: new Date().toISOString().split('T')[0], // Prazo de hoje
          created_by: user.user?.id, // RH que criou
          clicksign_assinado: 'pendente',
          documento_cpf_nome: `Proposta_${nomeModeloProposta.replace(/\s+/g, '_')}_${cpfLimpo}.pdf`
        };

        const { error: propostaError } = await supabase
          .from('contratacao_contratos')
          .insert(proposta);

        if (propostaError) throw propostaError;
      }

      toast({
        title: "Sucesso",
        description: "Proposta salva na base de dados!",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao salvar proposta",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const savePropostasToTable = async () => {
    try {
      const { data: user, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // Buscar dados pessoais se existirem
      const { data: dadosPessoais } = await supabase
        .from('dados_pessoais')
        .select('*')
        .eq('cpf', formData.cpf)
        .maybeSingle();

      // Buscar dados detalhados dos modelos de proposta selecionados
      const { data: modelosDetalhados, error: modelosError } = await supabase
        .from('modelo_proposta')
        .select('id, nome_modelo, conteudo_html')
        .in('id', formData.modelos_proposta_selecionados);

      if (modelosError) throw modelosError;

      // Buscar o user_id do candidato pela contratação
      const { data: contratacaoData } = await supabase
        .from('contratacao')
        .select('id_usuario, user_criado, email, org_id')
        .eq('id', contratacaoId)
        .single();

      // Determinar o user_contratado (candidato)
      let userContratado = contratacaoData?.id_usuario || contratacaoData?.user_criado;
      
      // Se não encontrou user_id, buscar pelo email
      if (!userContratado && contratacaoData?.email) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('email', contratacaoData.email)
          .maybeSingle();
        
        userContratado = profileData?.user_id;
      }

      // Criar ou atualizar propostas na tabela contratacao_propostas
      for (const modelo of modelosDetalhados || []) {
        const htmlSubstituido = await substituirDadosContrato(modelo.conteudo_html, dadosPessoais);
        
        // Gerar nome do documento com nome do modelo e CPF
        const cpfLimpo = formData.cpf.replace(/[^\d]/g, '');
        const nomeDocumento = `Proposta_${modelo.nome_modelo.replace(/\s+/g, '_')}_${cpfLimpo}.pdf`;
        
        // Usar data padrão se prazo_assinatura não estiver definido
        const prazoFinal = formData.prazo_assinatura || new Date().toISOString().split('T')[0];

        // Verificar se já existe proposta para este modelo
        const { data: existingProposta } = await supabase
          .from('contratacao_propostas')
          .select('id')
          .eq('contratacao_id', contratacaoId)
          .eq('modelo_proposta_id', modelo.id)
          .maybeSingle();

        if (existingProposta) {
          // ATUALIZAR proposta existente
          const { error: updateError } = await supabase
            .from('contratacao_propostas')
            .update({
              html_proposta: htmlSubstituido,
              documento_cpf_nome: nomeDocumento,
              prazo: prazoFinal,
              updated_at: new Date().toISOString(),
              updated_by: user.user?.id
            })
            .eq('id', existingProposta.id);

          if (updateError) {
            console.error('Erro ao atualizar proposta:', updateError);
            toast({
              title: "Erro ao atualizar proposta",
              description: `${modelo.nome_modelo}: ${updateError.message}`,
              variant: "destructive"
            });
            throw updateError;
          }

          console.log(`Proposta ${modelo.nome_modelo} ATUALIZADA com sucesso`);
        } else {
          // INSERIR nova proposta
          const proposta = {
            contratacao_id: contratacaoId,
            user_contratado: userContratado,
            modelo_proposta_id: modelo.id,
            html_proposta: htmlSubstituido,
            bt_aceite: false,
            prazo: prazoFinal,
            created_by: user.user?.id,
            documento_cpf_nome: nomeDocumento,
            clicksign_assinado: 'pendente',
            org_id: contratacaoData?.org_id
          };

          const { error: insertError } = await supabase
            .from('contratacao_propostas')
            .insert(proposta);

          if (insertError) {
            console.error('Erro ao inserir proposta:', insertError);
            toast({
              title: "Erro ao criar proposta",
              description: `${modelo.nome_modelo}: ${insertError.message}`,
              variant: "destructive"
            });
            throw insertError;
          }

          console.log(`Proposta ${modelo.nome_modelo} CRIADA com sucesso`);
        }
      }

      toast({
        title: "Sucesso",
        description: "Propostas criadas e salvas na base de dados!",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao criar propostas",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const saveContratosToTable = async () => {
    try {
      const { data: user, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // Buscar dados pessoais se existirem
      const { data: dadosPessoaisData } = await supabase
        .from('dados_pessoais')
        .select('*')
        .eq('cpf', formData.cpf)
        .maybeSingle();

      // Buscar dados detalhados dos modelos selecionados
      const { data: modelosDetalhados, error: modelosError } = await supabase
        .from('tbl_modelo_contrato')
        .select('id, nome_modelo, conteudo_html')
        .in('id', formData.modelos_selecionados);

      if (modelosError) throw modelosError;

      // Buscar o user_id do candidato pela contratação
      const { data: contratacaoData } = await supabase
        .from('contratacao')
        .select('id_usuario, user_criado, email, org_id')
        .eq('id', contratacaoId)
        .single();

      // Determinar o user_contratado (candidato)
      let userContratado = contratacaoData?.id_usuario || contratacaoData?.user_criado;
      
      // Se não encontrou user_id, buscar pelo email
      if (!userContratado && contratacaoData?.email) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('email', contratacaoData.email)
          .maybeSingle();
        
        userContratado = profileData?.user_id;
      }

      // Criar/atualizar contratos na tabela contratacao_contratos (UPSERT)
      for (const modelo of modelosDetalhados || []) {
        const htmlSubstituido = await substituirDadosContrato(modelo.conteudo_html, dadosPessoaisData);
        
        // Gerar nome do documento com nome do modelo e CPF
        const cpfLimpo = formData.cpf.replace(/[^\d]/g, '');
        const nomeDocumento = `Contrato_${modelo.nome_modelo.replace(/\s+/g, '_')}_${cpfLimpo}.pdf`;
        
        // Verificar se já existe contrato para este modelo
        const { data: existingContrato } = await supabase
          .from('contratacao_contratos')
          .select('id')
          .eq('contratacao_id', contratacaoId)
          .eq('modelo_contrato_id', modelo.id)
          .maybeSingle();

        if (existingContrato) {
          // Atualizar contrato existente
          console.log(`📝 Atualizando contrato existente: ${modelo.nome_modelo}`);
          const { error: updateError } = await supabase
            .from('contratacao_contratos')
            .update({
              html_contrato: htmlSubstituido,
              prazo: formData.prazo_assinatura,
              documento_cpf_nome: nomeDocumento,
              updated_by: user.user?.id,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingContrato.id);

          if (updateError) throw updateError;
        } else {
          // Criar novo contrato
          console.log(`➕ Criando novo contrato: ${modelo.nome_modelo}`);
          const contrato = {
            contratacao_id: contratacaoId,
            user_contratado: userContratado,
            modelo_contrato_id: modelo.id,
            html_contrato: htmlSubstituido,
            bt_aceite: false,
            prazo: formData.prazo_assinatura,
            created_by: user.user?.id,
            documento_cpf_nome: nomeDocumento,
            clicksign_assinado: 'pendente',
            org_id: contratacaoData?.org_id
          };

          const { error: contratoError } = await supabase
            .from('contratacao_contratos')
            .insert(contrato);

          if (contratoError) throw contratoError;
        }
      }

      toast({
        title: "Sucesso",
        description: "Contratos criados/atualizados na base de dados!",
      });
    } catch (error: any) {
      console.error('❌ Erro ao salvar contratos:', error);
      toast({
        title: "Erro ao criar contratos",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const saveStep = async (step: string) => {
    try {
      setLoading(true);
      
      const updateData: any = {};
      
      if (step === 'dados') {
        updateData.email = formData.email;
        updateData.full_name = formData.nome_completo;
        updateData.nome_completo = formData.nome_completo;
        updateData.cpf = formData.cpf;
        updateData.celular = formData.celular;
        updateData.cargo_id = formData.cargo_id || null;
        updateData.salario = formData.salario ? parseFloat(formData.salario) : null;
        updateData.jornada_trabalho = formData.jornada_trabalho;
        updateData.horario_entrada = formData.horario_entrada;
        updateData.horario_saida = formData.horario_saida;
        updateData.data_inicio = formData.data_inicio;
        updateData.razao_social = formData.razao_social;
        updateData.cnpj = formData.cnpj;
        updateData.observacoes = formData.observacoes;
      }
      
      if (step === 'proposta') {
        updateData.ids_modelos_proposta = formData.modelos_proposta_selecionados;
      }
      
      if (step === 'vale_transporte') {
        const vtData = formData.valeTransporteData;
        
        // Calcular totais
        const totalIda = (vtData.ida_integracao_onibus_metro_valor || 0) + 
                        (vtData.ida_integracao_onibus_trem_valor || 0) + 
                        (vtData.ida_onibus_municipal_valor || 0) + 
                        (vtData.ida_onibus_intermunicipal_valor || 0) + 
                        (vtData.ida_metro_trem_valor || 0) + 
                        (vtData.ida_outros_valor || 0);
        const totalVolta = (vtData.volta_integracao_onibus_metro_valor || 0) + 
                          (vtData.volta_integracao_onibus_trem_valor || 0) + 
                          (vtData.volta_onibus_municipal_valor || 0) + 
                          (vtData.volta_onibus_intermunicipal_valor || 0) + 
                          (vtData.volta_metro_trem_valor || 0) + 
                          (vtData.volta_outros_valor || 0);
        const totalDiario = totalIda + totalVolta;
        
        // Validar se o total excede R$ 21,00
        if (totalDiario > 21) {
          const confirmar = window.confirm(
            `O valor total do vale transporte (R$ ${totalDiario.toFixed(2)}) excede o limite esperado de R$ 21,00. Deseja continuar?`
          );
          if (!confirmar) {
            setLoading(false);
            return;
          }
        }
        
        updateData.vale_transporte_valor_ida = totalIda;
        updateData.vale_transporte_valor_volta = totalVolta;
        
        console.log('Salvando dados detalhados do vale transporte:', vtData);
        
        // Buscar dados da contratação
        const { data: contratacaoData, error: contratacaoError } = await supabase
          .from('contratacao')
          .select('id_usuario, user_criado, org_id')
          .eq('id', contratacaoId)
          .single();
        
        if (!contratacaoError) {
          // Usar contratacao_id como referência principal (sempre existe)
          const userId = contratacaoData?.id_usuario || contratacaoData?.user_criado;
          
          const { error: vtError } = await supabase
            .from('vale_transporte_detalhado')
            .upsert({
              contratacao_id: contratacaoId,
              user_id: userId || null,
              org_id: contratacaoData?.org_id,
              total_ida: totalIda,
              total_volta: totalVolta,
              total_diario: totalDiario,
              ...vtData
            }, { onConflict: 'contratacao_id' });

          if (vtError) {
            console.error('Erro ao salvar vale transporte detalhado:', vtError);
            toast({
              variant: "destructive",
              title: "Erro ao salvar vale transporte detalhado",
              description: vtError.message
            });
            throw vtError;
          }
          
          console.log('Dados do vale transporte detalhado salvos com sucesso');
          toast({
            title: "Vale transporte salvo",
            description: "Os dados do vale transporte foram salvos com sucesso!"
          });
        }
      }
      
      if (step === 'contrato') {
        updateData.ids_modelos_contrato = formData.modelos_selecionados;
        updateData.prazo_assinatura = formData.prazo_assinatura;
      }
      
      if (step === 'exame') {
        updateData.endereco_exame = formData.endereco_exame;
        updateData.horario_exame = formData.horario_exame;
        updateData.stage = 'exame_admissional';
      }

      if (step === 'integracao') {
        updateData.quem_recebera_contratado = formData.quem_recebera_contratado;
        updateData.observacoes = formData.observacoes;
      }

      console.log('💾 Dados que serão salvos:', updateData);

      const { error } = await supabase
        .from('contratacao')
        .update(updateData)
        .eq('id', contratacaoId);

      if (error) throw error;
      
      // Atualizar dados iniciais
      setInitialFormData({ ...formData });
      setHasChanges(false);
      
      toast({
        title: "Sucesso",
        description: "Alterações salvas com sucesso!",
      });
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndNext = async () => {
    try {
      setLoading(true);
      
      const updateData: any = {};
      
      // Determinar quais dados salvar baseado na aba ativa
      if (activeTab === 'dados') {
        updateData.email = formData.email;
        updateData.full_name = formData.nome_completo;
        updateData.nome_completo = formData.nome_completo;
        updateData.cpf = formData.cpf;
        updateData.celular = formData.celular;
        updateData.cargo_id = formData.cargo_id || null;
        updateData.salario = formData.salario ? parseFloat(formData.salario) : null;
        updateData.jornada_trabalho = formData.jornada_trabalho;
        updateData.horario_entrada = formData.horario_entrada;
        updateData.horario_saida = formData.horario_saida;
        updateData.data_inicio = formData.data_inicio;
        updateData.razao_social = formData.razao_social;
        updateData.cnpj = formData.cnpj;
        updateData.observacoes = formData.observacoes;
        
        console.log('💾 Salvando aba DADOS:', updateData);
      }
      
      if (activeTab === 'proposta') {
        updateData.ids_modelos_proposta = formData.modelos_proposta_selecionados;
        
        console.log('💾 Salvando aba PROPOSTA:', {
          updateData,
          modelosSelecionados: formData.modelos_proposta_selecionados,
          quantidadeModelos: formData.modelos_proposta_selecionados.length
        });
        
        // Salvar propostas na tabela contratacao_propostas
        if (formData.modelos_proposta_selecionados.length > 0) {
          console.log('🚀 Chamando savePropostasToTable...');
          await savePropostasToTable();
        }
      }
      
      if (activeTab === 'vale_transporte') {
        const vtData = formData.valeTransporteData;
        
        console.log('💾 Salvando Vale Transporte - Dados:', vtData);
        
        // Calcular totais
        const totalIda = (vtData.ida_integracao_onibus_metro_valor || 0) + 
                        (vtData.ida_integracao_onibus_trem_valor || 0) + 
                        (vtData.ida_onibus_municipal_valor || 0) + 
                        (vtData.ida_onibus_intermunicipal_valor || 0) + 
                        (vtData.ida_metro_trem_valor || 0) + 
                        (vtData.ida_outros_valor || 0);
        const totalVolta = (vtData.volta_integracao_onibus_metro_valor || 0) + 
                          (vtData.volta_integracao_onibus_trem_valor || 0) + 
                          (vtData.volta_onibus_municipal_valor || 0) + 
                          (vtData.volta_onibus_intermunicipal_valor || 0) + 
                          (vtData.volta_metro_trem_valor || 0) + 
                          (vtData.volta_outros_valor || 0);
        const totalDiario = totalIda + totalVolta;
        
        console.log('💰 Totais calculados - Ida:', totalIda, 'Volta:', totalVolta, 'Total:', totalDiario);
        
        // Validar se o total excede R$ 21,00
        if (totalDiario > 21) {
          const confirmar = window.confirm(
            `O valor total do vale transporte (R$ ${totalDiario.toFixed(2)}) excede o limite esperado de R$ 21,00. Deseja continuar?`
          );
          if (!confirmar) {
            setLoading(false);
            return;
          }
        }
        
        // Manter compatibilidade com campos antigos
        updateData.vale_transporte_valor_ida = totalIda;
        updateData.vale_transporte_valor_volta = totalVolta;
        
        console.log('🔍 Buscando dados da contratação para salvar vale transporte...');
        
        // Salvar dados detalhados na tabela vale_transporte_detalhado
        const { data: contratacaoData, error: contratacaoError } = await supabase
          .from('contratacao')
          .select('id_usuario, user_criado, org_id')
          .eq('id', contratacaoId)
          .single();
        
        if (contratacaoError) {
          console.error('❌ Erro ao buscar dados da contratação:', contratacaoError);
        } else {
          console.log('📋 Dados da contratação encontrados:', contratacaoData);
        }
        
        // Usar contratacao_id como referência principal (sempre existe)
        const userId = contratacaoData?.id_usuario || contratacaoData?.user_criado;
        
        console.log('💾 Salvando dados detalhados do vale transporte...');
        const { error: vtError } = await supabase
          .from('vale_transporte_detalhado')
          .upsert({
            contratacao_id: contratacaoId,
            user_id: userId || null,
            org_id: contratacaoData?.org_id,
            total_ida: totalIda,
            total_volta: totalVolta,
            total_diario: totalDiario,
            ...vtData
          }, { onConflict: 'contratacao_id' });
        
        if (vtError) {
          console.error('❌ Erro ao salvar vale transporte detalhado:', vtError);
          toast({
            title: "Erro ao salvar vale transporte detalhado",
            description: vtError.message,
            variant: "destructive",
          });
          throw vtError;
        }
        
        console.log('✅ Vale transporte detalhado salvo com sucesso!');
        toast({
          title: "Vale Transporte Salvo",
          description: "Dados detalhados do vale transporte salvos com sucesso!",
        });
      }
      
      if (activeTab === 'contrato') {
        // Validação obrigatória do prazo quando há modelos selecionados
        if (formData.modelos_selecionados.length > 0 && !formData.prazo_assinatura) {
          toast({
            title: "Campo obrigatório",
            description: "Por favor, informe o Prazo de Assinatura antes de salvar os contratos.",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }
        
        updateData.ids_modelos_contrato = formData.modelos_selecionados;
        updateData.prazo_assinatura = formData.prazo_assinatura;
        
        console.log('💾 Salvando aba CONTRATO:', updateData);
        
        // Salvar contratos selecionados na nova tabela contratacao_contratos
        if (formData.modelos_selecionados.length > 0 && formData.prazo_assinatura) {
          await saveContratosToTable();
        }
      }
      
      if (activeTab === 'exame') {
        updateData.endereco_exame = formData.endereco_exame;
        updateData.horario_exame = formData.horario_exame;
        
        console.log('💾 Salvando aba EXAME:', updateData);
      }

      if (activeTab === 'integracao') {
        updateData.quem_recebera_contratado = formData.quem_recebera_contratado;
        updateData.observacoes = formData.observacoes;
        
        console.log('💾 Salvando aba INTEGRAÇÃO:', updateData);
      }

      const { error } = await supabase
        .from('contratacao')
        .update(updateData)
        .eq('id', contratacaoId);

      if (error) {
        console.error('❌ Erro ao atualizar contratacao:', error);
        throw error;
      }
      
      console.log('✅ Dados salvos na tabela contratacao:', updateData);

      // Buscar dados do contratante e preposta para o webhook (apenas para proposta)
      let dadosContratante = null;
      if (activeTab === 'proposta') {
        try {
          const { data: contratacaoData } = await supabase
            .from('contratacao')
            .select('nome_contratante, email_contratante, numero_contratante, nome_preposta, email_preposta, numero_preposta')
            .eq('id', contratacaoId)
            .maybeSingle();
          
          // Usar dados do contratante ou da preposta como fallback
          dadosContratante = {
            nome_contratante: contratacaoData?.nome_contratante || contratacaoData?.nome_preposta || '',
            email_contratante: contratacaoData?.email_contratante || contratacaoData?.email_preposta || '',
            numero_contratante: contratacaoData?.numero_contratante || contratacaoData?.numero_preposta || ''
          };
          
          console.log('🔍 Dados do contratante/preposta encontrados:', dadosContratante);
        } catch (error) {
          console.error('Erro ao buscar dados do contratante:', error);
          dadosContratante = {
            nome_contratante: '',
            email_contratante: '',
            numero_contratante: ''
          };
        }

        // Validar se há dados mínimos necessários
        if (!dadosContratante.email_contratante) {
          toast({
            title: "Dados incompletos",
            description: "É necessário informar o email do contratante ou da preposta antes de enviar a proposta.",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }

        console.log('🔄 Aba ativa:', activeTab);
        console.log('📋 Proposta personalizada existe:', !!propostaPersonalizada);

        // Enviar dados para o webhook (Proposta) - CADA PROPOSTA COMO DOCUMENTO SEPARADO
        console.log('📤 Enviando propostas individuais para o webhook...');
        
        // Limpar números de telefone (remover parênteses, espaços e hífens)
        const limparTelefone = (telefone: string) => {
          if (!telefone) return '';
          return telefone.replace(/[^\d]/g, ''); // Remove tudo exceto dígitos
        };
        
        try {
          // Buscar TODAS as propostas individuais da tabela contratacao_propostas
          const { data: propostasData, error: propostasError } = await supabase
            .from('contratacao_propostas')
            .select('id, html_proposta, documento_cpf_nome, modelo_proposta_id')
            .eq('contratacao_id', contratacaoId);

          if (propostasError) {
            throw propostasError;
          }

          console.log('📋 Propostas encontradas para envio:', propostasData?.length || 0);

          if (!propostasData || propostasData.length === 0) {
            toast({
              title: "Aviso",
              description: "Nenhuma proposta encontrada para envio. Salve as propostas primeiro.",
              variant: "default",
            });
            setLoading(false);
            return;
          }

          // Buscar dados pessoais para reprocessamento das variáveis
          const { data: dadosPessoaisEnvio } = await supabase
            .from('dados_pessoais')
            .select('*')
            .eq('cpf', formData.cpf)
            .maybeSingle();

          console.log('📋 Dados pessoais para reprocessamento:', dadosPessoaisEnvio ? 'encontrados' : 'não encontrados');

          // Enviar cada proposta individualmente (igual aos contratos)
          let envioConcluido = 0;
          let errosEnvio = 0;

          for (const proposta of propostasData) {
            console.log(`📤 Enviando proposta ${envioConcluido + 1}/${propostasData.length}: ${proposta.documento_cpf_nome}`);
            
            // REPROCESSAR HTML para garantir substituição de todas as variáveis
            // Isso resolve o problema de propostas salvas com código antigo
            // Passa contratacaoId para buscar cargo_id diretamente do banco
            const htmlReprocessado = await substituirDadosContrato(proposta.html_proposta, dadosPessoaisEnvio, contratacaoId);
            console.log(`🔄 HTML reprocessado para proposta ${proposta.id}`);
            
            const webhookData = {
              nome_contratante: dadosContratante.nome_contratante,
              email_contratante: dadosContratante.email_contratante, 
              numero_contratante: limparTelefone(dadosContratante.numero_contratante),
              nome_contratado: formData.nome_completo,
              email_contratado: formData.email,
              numero_contratado: limparTelefone(formData.celular || ""),
              html_proposta: htmlReprocessado,
              documento_cpf: proposta.documento_cpf_nome,
              proposta_id: proposta.id,
              modelo_proposta_id: proposta.modelo_proposta_id
            };

            console.log('📋 Dados enviados para proposta:', {
              ...webhookData,
              html_proposta: `[HTML ${webhookData.html_proposta?.length || 0} chars]`
            });

            try {
              // Usar edge function igual aos contratos
              const { data: response, error: invokeError } = await supabase.functions.invoke('enviar-propostas-webhook', {
                body: webhookData
              });

              if (invokeError) {
                console.error(`❌ Erro ao enviar proposta ${proposta.id}:`, invokeError);
                errosEnvio++;
                continue;
              }

              console.log('📧 RESPOSTA DO WEBHOOK:', JSON.stringify(response, null, 2));

              // Extrair document_id da resposta
              let documentId = null;
              const responseData = response?.data || response;
              
              if (responseData?.envelope_id) {
                documentId = responseData.envelope_id;
              } else if (responseData?.envelopeId) {
                documentId = responseData.envelopeId;
              } else if (responseData?.id) {
                documentId = responseData.id;
              } else if (responseData?.key) {
                documentId = responseData.key;
              } else if (responseData?.data?.envelope_id) {
                documentId = responseData.data.envelope_id;
              } else if (responseData?.data?.id) {
                documentId = responseData.data.id;
              } else if (responseData?.result?.envelope_id) {
                documentId = responseData.result.envelope_id;
              } else if (responseData?.result?.id) {
                documentId = responseData.result.id;
              } else if (responseData?.envelope?.id) {
                documentId = responseData.envelope.id;
              } else if (responseData?.document?.key) {
                documentId = responseData.document.key;
              } else {
                // Última tentativa: regex para encontrar UUID
                const responseStr = JSON.stringify(responseData);
                const envelopeMatch = responseStr.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/);
                if (envelopeMatch) {
                  documentId = envelopeMatch[0];
                }
              }

              console.log(`🆔 Document ID extraído para proposta ${proposta.id}:`, documentId);

              // Salvar document_id na proposta
              if (documentId) {
                const { error: updateError } = await supabase
                  .from('contratacao_propostas')
                  .update({ 
                    clicksign_document_id: documentId,
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', proposta.id);

                if (updateError) {
                  console.error(`❌ Erro ao salvar document_id para proposta ${proposta.id}:`, updateError);
                } else {
                  console.log(`✅ Document ID salvo para proposta ${proposta.id}`);
                }

                // Salvar primeiro document_id na tabela contratacao (compatibilidade)
                if (envioConcluido === 0) {
                  await supabase
                    .from('contratacao')
                    .update({ 
                      proposta_clicksign_document_id: documentId,
                      updated_at: new Date().toISOString()
                    })
                    .eq('id', contratacaoId);
                }
              }

              envioConcluido++;
            } catch (propostaError) {
              console.error(`❌ Erro ao processar proposta ${proposta.id}:`, propostaError);
              errosEnvio++;
            }
          }

          console.log(`✅ Envio concluído: ${envioConcluido} sucesso, ${errosEnvio} erros`);
          
          if (envioConcluido > 0) {
            toast({
              title: "Sucesso",
              description: `${envioConcluido} proposta(s) enviada(s) para assinatura!${errosEnvio > 0 ? ` (${errosEnvio} erro(s))` : ''}`,
            });
          } else {
            toast({
              title: "Erro",
              description: "Nenhuma proposta foi enviada com sucesso.",
              variant: "destructive",
            });
          }
          
          // Aguardar um pouco e verificar status automaticamente
          setTimeout(async () => {
            try {
              console.log('🔍 Verificando status automaticamente após 5 segundos...');
              await supabase.functions.invoke('verificar-status-clicksign', {
                body: {}
              });
            } catch (error) {
              console.error('❌ Erro na verificação automática:', error);
            }
          }, 5000);
        } catch (webhookError) {
          console.error('Erro ao enviar webhook:', webhookError);
          toast({
            title: "Aviso",
            description: "Dados salvos, mas houve um erro ao processar a integração externa.",
            variant: "default",
          });
        }
      }
      
      // Atualizar dados iniciais após salvar
      setInitialFormData({ ...formData });
      
      toast({
        title: "Sucesso",
        description: "Alterações salvas com sucesso!",
      });
      
      // Avançar para próxima etapa automaticamente
      const tabs = ['dados', 'proposta', 'exame', 'contrato', 'integracao'];
      const currentIndex = tabs.indexOf(activeTab);
      
      if (currentIndex < tabs.length - 1) {
        setActiveTab(tabs[currentIndex + 1]);
      }
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEnviarContrato = async () => {
    try {
      setLoading(true);

      // Buscar dados do contratante e preposta para o webhook
      let dadosContratante = null;
      try {
        const { data: contratacaoData } = await supabase
          .from('contratacao')
          .select('nome_contratante, email_contratante, numero_contratante, nome_preposta, email_preposta, numero_preposta')
          .eq('id', contratacaoId)
          .maybeSingle();
        
        // Usar dados do contratante ou da preposta como fallback
        dadosContratante = {
          nome_contratante: contratacaoData?.nome_contratante || contratacaoData?.nome_preposta || '',
          email_contratante: contratacaoData?.email_contratante || contratacaoData?.email_preposta || '',
          numero_contratante: contratacaoData?.numero_contratante || contratacaoData?.numero_preposta || ''
        };
        
        console.log('🔍 Dados do contratante/preposta encontrados:', dadosContratante);
      } catch (error) {
        console.error('Erro ao buscar dados do contratante:', error);
        dadosContratante = {
          nome_contratante: '',
          email_contratante: '',
          numero_contratante: ''
        };
      }

      // Validar se há dados mínimos necessários
      if (!dadosContratante.email_contratante) {
        toast({
          title: "Dados incompletos",
          description: "É necessário informar o email do contratante ou da preposta antes de enviar os contratos.",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      // Enviar contratos para o webhook
      if (formData.modelos_selecionados.length > 0) {
        console.log('📦 Enviando contratos para o webhook...');
        
        // Validação do prazo obrigatório
        if (!formData.prazo_assinatura) {
          toast({
            title: "Prazo obrigatório",
            description: "Informe o Prazo de Assinatura antes de enviar os contratos.",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }
        
        try {
          // Buscar contratos HTML da tabela contratacao_contratos
          let { data: contratosData } = await supabase
            .from('contratacao_contratos')
            .select('html_contrato, modelo_contrato_id, documento_cpf_nome')
            .eq('contratacao_id', contratacaoId);

          console.log('📋 Contratos encontrados:', contratosData?.length || 0);
          
          // Se não existem contratos, criar automaticamente antes de enviar
          if (!contratosData || contratosData.length === 0) {
            console.log('⚠️ Nenhum contrato encontrado, criando automaticamente...');
            await saveContratosToTable();
            
            // Buscar novamente após criar
            const { data: newContratosData } = await supabase
              .from('contratacao_contratos')
              .select('html_contrato, modelo_contrato_id, documento_cpf_nome')
              .eq('contratacao_id', contratacaoId);
            
            contratosData = newContratosData;
            console.log('📋 Contratos após criação automática:', contratosData?.length || 0);
          }
          
          // Limpar números de telefone (remover parênteses, espaços e hífens)
          const limparTelefone = (telefone: string) => {
            if (!telefone) return '';
            return telefone.replace(/[^\d]/g, ''); // Remove tudo exceto dígitos
          };
          
          if (contratosData && contratosData.length > 0) {
            for (const contrato of contratosData) {
              console.log('📤 Enviando contrato individual...');
              
              // Buscar nome do modelo de contrato para fallback
              const modeloContrato = modelos.find(m => m.id === contrato.modelo_contrato_id);
              const nomeModeloContrato = modeloContrato?.nome_modelo || 'Contrato';
              const cpfLimpo = formData.cpf.replace(/[^\d]/g, '');
              
              const webhookData = {
                nome_contratante: dadosContratante.nome_contratante,
                email_contratante: dadosContratante.email_contratante,
                numero_contratante: limparTelefone(dadosContratante.numero_contratante), 
                html_proposta: contrato.html_contrato, // Usando o HTML do contrato
                nome_contratado: formData.nome_completo,
                email_contratado: formData.email,
                numero_contratado: limparTelefone(formData.celular || ""),
                documento_cpf: contrato.documento_cpf_nome || `Contrato_${nomeModeloContrato.replace(/\s+/g, '_')}_${cpfLimpo}.pdf`
              };

              console.log('📋 Dados do contrato que serão enviados:', webhookData);

              console.log('🌐 Enviando para webhook via Supabase function');
              
              const { data: response, error: webhookError } = await supabase.functions.invoke('enviar-contratos-webhook', {
                body: webhookData
              });

              if (webhookError) {
                throw new Error(`Erro na chamada do webhook: ${webhookError.message}`);
              }

              console.log('📡 Resposta do webhook:', response);

              if (!response.success) {
                console.error('❌ Erro do webhook:', response.error);
                throw new Error(`Erro na chamada do webhook: ${response.error}`);
              }

              // Processar resposta para obter o ID do documento do ClickSign
              const responseData = response.data;
              console.log('📧 RESPOSTA COMPLETA DO N8N (CONTRATO):', JSON.stringify(responseData, null, 2));

              // Buscar o envelope_id em todos os campos possíveis da resposta
              let documentId = null;
              
              if (responseData.envelope_id) {
                documentId = responseData.envelope_id;
                console.log('🆔 Envelope ID encontrado em: envelope_id');
              } else if (responseData.envelopeId) {
                documentId = responseData.envelopeId;
                console.log('🆔 Envelope ID encontrado em: envelopeId');
              } else if (responseData.id) {
                documentId = responseData.id;
                console.log('🆔 Envelope ID encontrado em: id');
              } else if (responseData.key) {
                documentId = responseData.key;
                console.log('🆔 Envelope ID encontrado em: key');
              } else if (responseData.data && responseData.data.envelope_id) {
                documentId = responseData.data.envelope_id;
                console.log('🆔 Envelope ID encontrado em: data.envelope_id');
              } else if (responseData.data && responseData.data.id) {
                documentId = responseData.data.id;
                console.log('🆔 Envelope ID encontrado em: data.id');
              } else if (responseData.result && responseData.result.envelope_id) {
                documentId = responseData.result.envelope_id;
                console.log('🆔 Envelope ID encontrado em: result.envelope_id');
              } else if (responseData.result && responseData.result.id) {
                documentId = responseData.result.id;
                console.log('🆔 Envelope ID encontrado em: result.id');
              } else if (responseData.envelope && responseData.envelope.id) {
                documentId = responseData.envelope.id;
                console.log('🆔 Envelope ID encontrado em: envelope.id');
              } else {
                // Como última tentativa, extrair do log do N8N se possível
                const responseStr = JSON.stringify(responseData);
                const envelopeMatch = responseStr.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/);
                if (envelopeMatch) {
                  documentId = envelopeMatch[0];
                  console.log('🆔 Envelope ID extraído por regex:', documentId);
                }
              }

              console.log('🆔 ID DO DOCUMENTO FINAL (CONTRATO):', documentId);

              if (documentId) {
                console.log('💾 Salvando ID do documento do contrato no banco...');
                const { error: updateError } = await supabase
                  .from('contratacao_contratos')
                  .update({ 
                    clicksign_document_id: documentId,
                    updated_at: new Date().toISOString()
                  })
                  .eq('contratacao_id', contratacaoId)
                  .eq('modelo_contrato_id', contrato.modelo_contrato_id);

                if (updateError) {
                  console.error('❌ Erro ao salvar ID do documento ClickSign do contrato:', updateError);
                } else {
                  console.log('✅ ID do documento ClickSign do contrato salvo com sucesso:', documentId);
                }
              }
            }

            console.log('Webhooks dos contratos enviados com sucesso!');
            
            toast({
              title: "Sucesso",
              description: "Contratos enviados para assinatura com sucesso!",
            });
            
            // Aguardar um pouco e verificar status automaticamente
            setTimeout(async () => {
              try {
                console.log('🔍 Verificando status automaticamente após 5 segundos...');
                await supabase.functions.invoke('verificar-status-clicksign', {
                  body: {}
                });
              } catch (error) {
                console.error('❌ Erro na verificação automática:', error);
              }
            }, 5000);
          } else {
            toast({
              title: "Aviso",
              description: "Nenhum contrato encontrado para envio.",
              variant: "default",
            });
          }
        } catch (webhookError) {
          console.error('❌ Erro detalhado ao enviar webhook dos contratos:', webhookError);
          
          let errorMessage = "Erro ao processar o envio dos contratos.";
          if (webhookError instanceof Error) {
            errorMessage = `Erro: ${webhookError.message}`;
          } else if (typeof webhookError === 'string') {
            errorMessage = webhookError;
          }
          
          toast({
            title: "Erro no Envio do Contrato",
            description: errorMessage,
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Aviso",
          description: "Nenhum modelo de contrato selecionado.",
          variant: "default",
        });
      }
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Edição sempre permitida independente do status/etapa atual

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger 
            value="dados"
            className={`${isTabValid('dados') ? 'bg-green-100 text-green-800' : ''} ${!canAccessTab('dados') ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!canAccessTab('dados')}
          >
            Dados
          </TabsTrigger>
          <TabsTrigger 
            value="proposta"
            className={`${isTabValid('proposta') ? 'bg-green-100 text-green-800' : ''} ${!canAccessTab('proposta') ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!canAccessTab('proposta')}
          >
            Proposta
          </TabsTrigger>
          <TabsTrigger 
            value="vale_transporte"
            className={`${isTabValid('vale_transporte') ? 'bg-green-100 text-green-800' : ''} ${!canAccessTab('vale_transporte') ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!canAccessTab('vale_transporte')}
          >
            Vale Transporte
          </TabsTrigger>
          <TabsTrigger 
            value="exame"
            className={`${isTabValid('exame') ? 'bg-green-100 text-green-800' : ''} ${!canAccessTab('exame') ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!canAccessTab('exame')}
          >
            Exame
          </TabsTrigger>
          <TabsTrigger 
            value="contrato"
            className={`${isTabValid('contrato') ? 'bg-green-100 text-green-800' : ''} ${!canAccessTab('contrato') ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!canAccessTab('contrato')}
          >
            Contrato
          </TabsTrigger>
          <TabsTrigger 
            value="integracao"
            className={`${isTabValid('integracao') ? 'bg-green-100 text-green-800' : ''} ${!canAccessTab('integracao') ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!canAccessTab('integracao')}
          >
            Integração
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dados" className="space-y-4">
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="exemplo@email.com"
            />
          </div>
          <div>
            <Label htmlFor="nome_completo">Nome Completo *</Label>
            <Input
              id="nome_completo"
              value={formData.nome_completo}
              onChange={(e) => setFormData(prev => ({ ...prev, nome_completo: e.target.value }))}
              placeholder="Nome completo do candidato"
            />
          </div>
          <div>
            <Label htmlFor="cpf">CPF *</Label>
            <Input
              id="cpf"
              value={formData.cpf}
              onChange={(e) => setFormData(prev => ({ ...prev, cpf: e.target.value }))}
              placeholder="000.000.000-00"
            />
          </div>
          <div>
            <Label htmlFor="celular">Celular</Label>
            <Input
              id="celular"
              type="tel"
              value={formData.celular}
              onChange={(e) => setFormData(prev => ({ ...prev, celular: e.target.value }))}
              placeholder="(11) 99999-9999"
            />
          </div>
          <div>
            <Label htmlFor="cargo">Cargo *</Label>
            <Select
              value={formData.cargo_id} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, cargo_id: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o cargo" />
              </SelectTrigger>
              <SelectContent>
                {cargos.map(cargo => (
                  <SelectItem key={cargo.id} value={cargo.id}>
                    {cargo.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="salario">Salário *</Label>
            <Input
              id="salario"
              type="number"
              step="0.01"
              max="50000"
              value={formData.salario}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                if (value <= 50000 || e.target.value === '') {
                  setFormData(prev => ({ ...prev, salario: e.target.value }));
                }
              }}
              placeholder="R$ 0,00"
            />
          </div>
          <div>
            <Label htmlFor="jornada">Jornada de Trabalho *</Label>
            <Select
              value={formData.jornada_trabalho} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, jornada_trabalho: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione a jornada" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="40h">40h semanais</SelectItem>
                <SelectItem value="44h">44h semanais</SelectItem>
                <SelectItem value="medio_periodo">Meio período</SelectItem>
                <SelectItem value="integral">Integral</SelectItem>
                <SelectItem value="flexivel">Flexível</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="horario_entrada">Horário de Entrada *</Label>
              <Input
                id="horario_entrada"
                type="time"
                value={formData.horario_entrada}
                onChange={(e) => setFormData(prev => ({ ...prev, horario_entrada: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="horario_saida">Horário de Saída *</Label>
              <Input
                id="horario_saida"
                type="time"
                value={formData.horario_saida}
                onChange={(e) => setFormData(prev => ({ ...prev, horario_saida: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="data_inicio">Data de Início *</Label>
            <Input
              id="data_inicio"
              type="date"
              value={formData.data_inicio}
              onChange={(e) => setFormData(prev => ({ ...prev, data_inicio: e.target.value }))}
            />
          </div>
        </TabsContent>

        <TabsContent value="proposta" className="space-y-4">
          <div>
            <Label>Modelos de Proposta * (Selecione um ou mais)</Label>
            <div className="space-y-2 max-h-60 overflow-y-auto border rounded-md p-3">
              {modelosPropostas.map(modelo => (
                <div key={modelo.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`proposta-${modelo.id}`}
                    checked={formData.modelos_proposta_selecionados.includes(modelo.id)}
                    onCheckedChange={() => handleModeloPropostaToggle(modelo.id)}
                  />
                  <Label 
                    htmlFor={`proposta-${modelo.id}`}
                    className="font-normal cursor-pointer"
                  >
                    {modelo.nome_modelo}
                  </Label>
                </div>
              ))}
            </div>
            {formData.modelos_proposta_selecionados.length > 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                {formData.modelos_proposta_selecionados.length} modelo(s) selecionado(s)
              </p>
            )}
          </div>
          
          {propostaPersonalizada && formData.modelos_proposta_selecionados.length > 0 && (
            <div className="mt-6">
              <Label>Pré-visualização das Propostas Selecionadas ({formData.modelos_proposta_selecionados.length})</Label>
              <div className="border rounded p-4 max-h-96 overflow-y-auto">
                <div 
                  dangerouslySetInnerHTML={{ __html: propostaPersonalizada }}
                  className="prose prose-sm max-w-none"
                />
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="vale_transporte" className="space-y-4">
          <h3 className="text-lg font-semibold">Vale Transporte</h3>
          <ValeTransporteForm 
            data={formData.valeTransporteData}
            onChange={(data) => {
              setFormData(prev => ({ ...prev, valeTransporteData: data }));
              setHasChanges(true);
            }}
          />
        </TabsContent>

        <TabsContent value="exame" className="space-y-4">
          <div>
            <Label htmlFor="endereco_exame">Endereço do Exame *</Label>
            <Input
              id="endereco_exame"
              value={formData.endereco_exame}
              onChange={(e) => setFormData(prev => ({ ...prev, endereco_exame: e.target.value }))}
              placeholder="Endereço onde será realizado o exame"
            />
          </div>
          <div>
            <Label htmlFor="horario_exame">Horário do Exame *</Label>
            <Input
              id="horario_exame"
              type="datetime-local"
              value={formData.horario_exame}
              onChange={(e) => setFormData(prev => ({ ...prev, horario_exame: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="guia_encaminhamento">Guia de Encaminhamento</Label>
            <Input
              id="guia_encaminhamento"
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  // Validar tamanho do arquivo (máximo 5MB)
                  if (file.size > 5 * 1024 * 1024) {
                    toast({
                      title: "Erro",
                      description: "O arquivo deve ter no máximo 5MB",
                      variant: "destructive",
                    });
                    return;
                  }
                  
                  try {
                    // Upload do arquivo para o Supabase Storage
                    const fileExt = file.name.split('.').pop();
                    const fileName = `guia_encaminhamento_${Date.now()}.${fileExt}`;
                    const filePath = `${formData.cpf}/${fileName}`;

                    const { data, error } = await supabase.storage
                      .from('contratacao-documentos')
                      .upload(filePath, file);

                    if (error) throw error;

                    // Salvar o caminho da guia na tabela contratacao
                    const { error: updateError } = await supabase
                      .from('contratacao')
                      .update({ guia_exame_path: filePath })
                      .eq('id', contratacaoId);

                    if (updateError) throw updateError;

                    setFormData(prev => ({ ...prev, guia_encaminhamento: file }));
                    
                    toast({
                      title: "Sucesso",
                      description: "Guia de encaminhamento enviada com sucesso!",
                    });
                  } catch (error: any) {
                    toast({
                      title: "Erro ao enviar arquivo",
                      description: error.message,
                      variant: "destructive",
                    });
                  }
                }
              }}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Formatos aceitos: PDF, DOC, DOCX, JPG, JPEG, PNG (máximo 5MB)
            </p>
          </div>
        </TabsContent>

        <TabsContent value="contrato" className="space-y-4">
          <div>
            <Label htmlFor="prazo_assinatura">
              Prazo Para Assinar o Contrato <span className="text-destructive">*</span>
            </Label>
            <Input
              id="prazo_assinatura"
              type="date"
              value={formData.prazo_assinatura}
              onChange={(e) => setFormData(prev => ({ ...prev, prazo_assinatura: e.target.value }))}
              className={!formData.prazo_assinatura && formData.modelos_selecionados.length > 0 ? 'border-destructive' : ''}
            />
            {!formData.prazo_assinatura && formData.modelos_selecionados.length > 0 && (
              <p className="text-sm text-destructive mt-1">Campo obrigatório para salvar contratos</p>
            )}
          </div>
          
          <div>
            <Label>Modelos de Contrato *</Label>
            <div className="space-y-2 max-h-40 overflow-y-auto border rounded p-2">
              {modelos.map(modelo => (
                <div key={modelo.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={modelo.id}
                    checked={formData.modelos_selecionados.includes(modelo.id)}
                    onCheckedChange={() => handleModeloToggle(modelo.id)}
                  />
                  <Label htmlFor={modelo.id} className="text-sm">
                    {modelo.nome_modelo}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {formData.modelos_selecionados.length > 0 && (
            <div className="space-y-8">
              <Label>Visualização dos Contratos Selecionados</Label>
              {formData.modelos_selecionados.map((modeloId, index) => {
                const modelo = modelos.find(m => m.id === modeloId);
                const modeloDetalhado = modelosDetalhados[modeloId];
                
                return (
                  <div key={modeloId} className="space-y-4">
                    {/* Separador visual entre contratos (exceto no primeiro) */}
                    {index > 0 && (
                      <div className="border-t-2 border-dashed border-gray-300 pt-6">
                        <div className="text-center text-sm text-gray-500 font-medium mb-4">
                          ━━━ DOCUMENTO {index + 1} ━━━
                        </div>
                      </div>
                    )}
                    
                    <div className="border-2 rounded-lg overflow-hidden shadow-lg bg-white">
                      <div className="bg-primary text-primary-foreground px-6 py-4 border-b">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-lg">CONTRATO: {modelo?.nome_modelo}</h4>
                          <span className="text-sm opacity-90">Documento {index + 1} de {formData.modelos_selecionados.length}</span>
                        </div>
                      </div>
                      
                      {modeloDetalhado ? (
                        <div className="bg-white">
                          {/* Container que simula uma página de documento */}
                          <div className="max-w-4xl mx-auto bg-white shadow-sm">
                            <div 
                              className="p-8 min-h-[600px] bg-white font-serif text-sm leading-relaxed text-gray-900"
                              style={{
                                fontFamily: 'Times, "Times New Roman", serif',
                                lineHeight: '1.6',
                                fontSize: '14px'
                              }}
                            >
                              <style>{`
                                .contract-document h1, .contract-document h2, .contract-document h3 {
                                  font-weight: bold;
                                  margin: 1rem 0;
                                  text-align: center;
                                }
                                .contract-document h1 { font-size: 18px; }
                                .contract-document h2 { font-size: 16px; }
                                .contract-document h3 { font-size: 14px; }
                                .contract-document p {
                                  margin: 0.5rem 0;
                                  text-align: justify;
                                  text-indent: 2rem;
                                }
                                .contract-document .signature-section {
                                  margin-top: 3rem;
                                  page-break-inside: avoid;
                                }
                                .contract-document table {
                                  width: 100%;
                                  border-collapse: collapse;
                                  margin: 1rem 0;
                                }
                                .contract-document table td, .contract-document table th {
                                  border: 1px solid #000;
                                  padding: 0.5rem;
                                  text-align: left;
                                }
                                .contract-document .date-location {
                                  text-align: right;
                                  margin: 1rem 0;
                                }
                              `}</style>
                              <div 
                                className="contract-document"
                                dangerouslySetInnerHTML={{ 
                                  __html: htmlProcessado[modeloId] || modeloDetalhado.html
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 text-center text-gray-500">
                          Carregando conteúdo do contrato...
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="integracao" className="space-y-4">
          <div>
            <Label htmlFor="quem_recebera_contratado">Quem irá receber o contratado *</Label>
            <Input
              id="quem_recebera_contratado"
              value={formData.quem_recebera_contratado}
              onChange={(e) => setFormData(prev => ({ ...prev, quem_recebera_contratado: e.target.value }))}
              placeholder="Nome da pessoa que irá receber o contratado"
            />
          </div>
          <div>
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea
              id="observacoes"
              value={formData.observacoes}
              onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
              placeholder="Digite observações adicionais sobre a contratação..."
              rows={3}
            />
          </div>
        </TabsContent>

      </Tabs>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        
        <div className="space-x-2">
          <Button 
            onClick={async () => {
              await saveStep(activeTab);
              setHasChanges(false);
            }}
            disabled={loading || !hasChanges}
            variant="outline"
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
          
          <Button 
            onClick={handleSaveAndNext}
            disabled={loading}
            variant="default"
          >
            {loading ? 'Salvando...' : 'Salvar e Avançar'}
          </Button>
          
          {activeTab === 'contrato' && formData.modelos_selecionados.length > 0 && (
            <Button 
              onClick={handleEnviarContrato}
              disabled={loading}
              variant="outline"
              className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600"
            >
              {loading ? 'Enviando...' : 'Enviar Contrato'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}